import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setUser, fetchUserProfile } from '../../store/slices/authSlice';
import { profileService, ProfileData, StoreData, CommissionRule } from '../../services/api/profileService';
import { AuthState } from '../../types';

// ─── constants ────────────────────────────────────────────────────────────────
const STORAGE_BASE = (import.meta.env.VITE_API_URL as string || 'https://kaoxet.net/api')
  .replace('/api', '/storage/');

const storageUrl = (path?: string | null) => {
  if (!path) return '/assets/logos/logo.svg';
  return path.startsWith('http') ? path : STORAGE_BASE + path;
};

const RULE_TABS: { key: 0 | 1 | 2 | 3; label: string }[] = [
  { key: 0, label: 'Recargas' },
  { key: 1, label: 'Paquetes' },
  { key: 2, label: 'Facturas' },
  { key: 3, label: 'Seguros' },
];

// ─── shared small components ─────────────────────────────────────────────────
const Field: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="font-medium text-gray-900 text-sm">{value || '—'}</p>
  </div>
);

const Alert: React.FC<{ type: 'success' | 'error'; message: string }> = ({ type, message }) => (
  <div className={`flex items-start gap-2 p-3 rounded-lg text-sm mb-4 ${type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
    {type === 'success'
      ? <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
      : <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
    }
    <span>{message}</span>
  </div>
);

const CardHeader: React.FC<{ gradient: string; icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({ gradient, icon, title, action }) => (
  <div className={`${gradient} px-6 py-4 flex items-center justify-between`}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">{icon}</div>
      <h2 className="text-white font-semibold">{title}</h2>
    </div>
    {action}
  </div>
);

const Spinner: React.FC<{ color?: string }> = ({ color = 'text-gray-400' }) => (
  <svg className={`animate-spin w-4 h-4 ${color}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const LabelInput: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean }> = ({ label, value, onChange, placeholder, type = 'text', disabled }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
);

// ─── password step indicator ──────────────────────────────────────────────────
type PwStep = 'idle' | 'sending' | 'code' | 'validating' | 'newpw' | 'saving';

const StepDot: React.FC<{ active: boolean; done: boolean; label: string }> = ({ active, done, label }) => (
  <div className="flex flex-col items-center gap-0.5">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done || active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
      {done ? '✓' : ''}
    </div>
    <span className={`text-[10px] whitespace-nowrap ${active || done ? 'text-purple-600' : 'text-gray-400'}`}>{label}</span>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isLoading } = useAppSelector((state) => state.auth as AuthState);

  useEffect(() => { dispatch(fetchUserProfile()); }, [dispatch]);

  // ── profile ───────────────────────────────────────────────────────────────
  const [editingProfile, setEditingProfile] = useState(false);
  const [pFirst, setPFirst]   = useState('');
  const [pLast, setPLast]     = useState('');
  const [pEmail, setPEmail]   = useState('');
  const [pIdCard, setPIdCard] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const prof = user as unknown as ProfileData | null;

  useEffect(() => {
    if (prof) {
      setPFirst(prof.first_name || '');
      setPLast(prof.last_name || '');
      setPEmail(prof.email || '');
      setPIdCard(prof.id_card || '');
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pFirst.trim() || !pLast.trim()) { setProfileMsg({ type: 'error', text: 'Nombre y apellido son obligatorios.' }); return; }
    setSavingProfile(true); setProfileMsg(null);
    try {
      const res = await profileService.editProfile({ first_name: pFirst, last_name: pLast, email: pEmail, id_card: pIdCard });
      if (!res.success) throw new Error(res.message || 'Error al guardar');
      dispatch(setUser({ ...user!, first_name: pFirst, last_name: pLast } as any));
      setProfileMsg({ type: 'success', text: 'Perfil actualizado correctamente.' });
      setEditingProfile(false);
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.message || 'Error al actualizar.' });
    } finally { setSavingProfile(false); }
  };

  // ── store ─────────────────────────────────────────────────────────────────
  const [store, setStore] = useState<StoreData | null>(null);
  const [loadingStore, setLoadingStore] = useState(false);
  const [editingStore, setEditingStore] = useState(false);
  const [sName, setSName]           = useState('');
  const [sAddress, setSAddress]     = useState('');
  const [sReference, setSReference] = useState('');
  const [sLat, setSLat]             = useState('');
  const [sLng, setSLng]             = useState('');
  const [savingStore, setSavingStore]   = useState(false);
  const [storeMsg, setStoreMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // logo upload
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchStore = useCallback(() => {
    setLoadingStore(true);
    profileService.getStore()
      .then(res => {
        if (res.success && res.data) {
          const s = res.data;
          setStore(s);
          setSName(s.name || '');
          setSAddress(s.address || '');
          setSReference(s.reference || '');
          setSLat(s.latitude || '');
          setSLng(s.longitude || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoadingStore(false));
  }, []);

  useEffect(() => { fetchStore(); }, [fetchStore]);

  const handleSaveStore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sName.trim() || !sAddress.trim()) { setStoreMsg({ type: 'error', text: 'Nombre y dirección son obligatorios.' }); return; }
    setSavingStore(true); setStoreMsg(null);
    try {
      const res = await profileService.editStore({ name: sName, address: sAddress, reference: sReference, latitude: sLat, longitude: sLng });
      if (!res.success) throw new Error(res.message || 'Error al guardar');
      setStore(prev => prev ? { ...prev, name: sName, address: sAddress, reference: sReference, latitude: sLat, longitude: sLng } : prev);
      setStoreMsg({ type: 'success', text: 'Comercio actualizado correctamente.' });
      setEditingStore(false);
    } catch (err: any) {
      setStoreMsg({ type: 'error', text: err.message || 'Error al actualizar.' });
    } finally { setSavingStore(false); }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // preview
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    // upload immediately
    setUploadingLogo(true); setStoreMsg(null);
    try {
      const res = await profileService.updateStoreImage(file);
      if (!res.success) throw new Error(res.message || 'Error al subir imagen');
      setStore(prev => prev ? { ...prev, storeImage: res.data || prev.storeImage } : prev);
      setStoreMsg({ type: 'success', text: 'Logo actualizado correctamente.' });
    } catch (err: any) {
      setLogoPreview(null);
      setStoreMsg({ type: 'error', text: err.message || 'Error al subir el logo.' });
    } finally { setUploadingLogo(false); }
  };

  // ── commission rules ──────────────────────────────────────────────────────
  const [ruleTab, setRuleTab] = useState<0 | 1 | 2 | 3>(0);
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);

  useEffect(() => {
    setLoadingRules(true);
    profileService.getCommissionRules(ruleTab)
      .then(res => setRules(res.success && res.data ? res.data : []))
      .catch(() => setRules([]))
      .finally(() => setLoadingRules(false));
  }, [ruleTab]);

  // ── password ──────────────────────────────────────────────────────────────
  const [pwStep, setPwStep]             = useState<PwStep>('idle');
  const [verCode, setVerCode]           = useState('');
  const [validatedCode, setValidatedCode] = useState('');
  const [newPw, setNewPw]               = useState('');
  const [confirmPw, setConfirmPw]       = useState('');
  const [pwMsg, setPwMsg]               = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSendCode = async () => {
    setPwStep('sending'); setPwMsg(null);
    try {
      const res = await profileService.sendChangePasswordCode();
      if (!res.success) throw new Error(res.message || 'Error');
      setPwStep('code'); setPwMsg({ type: 'success', text: 'Código enviado a su teléfono.' });
    } catch (err: any) { setPwMsg({ type: 'error', text: err.message }); setPwStep('idle'); }
  };

  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verCode.trim()) { setPwMsg({ type: 'error', text: 'Ingrese el código.' }); return; }
    setPwStep('validating'); setPwMsg(null);
    try {
      const res = await profileService.validateChangePasswordCode(verCode);
      if (!res.success) throw new Error(res.message || 'Código incorrecto');
      setValidatedCode(verCode); setPwStep('newpw');
    } catch (err: any) { setPwMsg({ type: 'error', text: err.message }); setPwStep('code'); }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPw || !confirmPw) { setPwMsg({ type: 'error', text: 'Complete todos los campos.' }); return; }
    if (newPw !== confirmPw)  { setPwMsg({ type: 'error', text: 'Las contraseñas no coinciden.' }); return; }
    if (newPw.length < 8)     { setPwMsg({ type: 'error', text: 'Mínimo 8 caracteres.' }); return; }
    setPwStep('saving'); setPwMsg(null);
    try {
      const res = await profileService.changePassword({ code: validatedCode, password: newPw, password_confirmation: confirmPw });
      if (!res.success) throw new Error(res.message || 'Error');
      setPwMsg({ type: 'success', text: '¡Contraseña cambiada correctamente!' });
      setPwStep('idle'); setVerCode(''); setValidatedCode(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) { setPwMsg({ type: 'error', text: err.message }); setPwStep('newpw'); }
  };

  const cancelPw = () => { setPwStep('idle'); setVerCode(''); setValidatedCode(''); setNewPw(''); setConfirmPw(''); setPwMsg(null); };

  // ── derived ───────────────────────────────────────────────────────────────
  const currentLogo = logoPreview || storageUrl(store?.storeImage);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Mi Perfil</h1>
        <p className="text-gray-500 text-sm">Administre su información personal y preferencias.</p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-blue-600 text-sm mb-4">
          <Spinner color="text-blue-600" /> Cargando perfil…
        </div>
      )}

      {/* ── Fila 1: Información Personal + Cambiar Contraseña ──────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* Información Personal */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <CardHeader
            gradient="bg-gradient-to-r from-blue-600 to-blue-500"
            icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
            title="Información Personal"
            action={!editingProfile ? (
              <button onClick={() => { setEditingProfile(true); setProfileMsg(null); }} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg transition">
                Editar
              </button>
            ) : undefined}
          />
          <div className="p-5">
            {profileMsg && <Alert type={profileMsg.type} message={profileMsg.text} />}
            {editingProfile ? (
              <form onSubmit={handleSaveProfile} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <LabelInput label="Nombre" value={pFirst} onChange={setPFirst} placeholder="Nombre" disabled={savingProfile} />
                  <LabelInput label="Apellido" value={pLast} onChange={setPLast} placeholder="Apellido" disabled={savingProfile} />
                </div>
                <LabelInput label="Email" type="email" value={pEmail} onChange={setPEmail} placeholder="correo@ejemplo.com" disabled={savingProfile} />
                <LabelInput label="Cédula" value={pIdCard} onChange={setPIdCard} placeholder="000-0000000-0" disabled={savingProfile} />
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={savingProfile} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium py-2 rounded-lg transition">
                    {savingProfile ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                  <button type="button" disabled={savingProfile} onClick={() => { setEditingProfile(false); setProfileMsg(null); }} className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm py-2 rounded-lg transition">
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="ID de usuario" value={prof?.user_id} />
                  <Field label="Teléfono" value={prof?.mobile_phone} />
                  <Field label="Nombre" value={prof?.first_name} />
                  <Field label="Apellido" value={prof?.last_name} />
                  <Field label="Email" value={prof?.email} />
                  <Field label="Cédula" value={prof?.id_card} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cambiar Contraseña */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <CardHeader
            gradient="bg-gradient-to-r from-purple-600 to-purple-500"
            icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
            title="Cambiar Contraseña"
          />
          <div className="p-5">
            {pwMsg && <Alert type={pwMsg.type} message={pwMsg.text} />}

            {pwStep !== 'idle' && pwStep !== 'sending' && (
              <div className="flex items-center gap-1 mb-5">
                <StepDot active done label="1. Código" />
                <div className="flex-1 h-px bg-gray-200" />
                <StepDot active={['newpw','saving'].includes(pwStep)} done={['newpw','saving'].includes(pwStep)} label="2. Validar" />
                <div className="flex-1 h-px bg-gray-200" />
                <StepDot active={pwStep === 'saving'} done={false} label="3. Nueva clave" />
              </div>
            )}

            {pwStep === 'idle' && (
              <div className="text-center py-5">
                <div className="w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </div>
                <p className="text-gray-500 text-sm mb-4">Por seguridad, te recomendamos cambiar tu contraseña regularmente.</p>
                <button onClick={handleSendCode} className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
                  Cambiar contraseña
                </button>
              </div>
            )}
            {pwStep === 'sending' && (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
                <Spinner color="text-purple-500" /> Enviando código…
              </div>
            )}
            {(pwStep === 'code' || pwStep === 'validating') && (
              <form onSubmit={handleValidateCode} className="space-y-4">
                <p className="text-sm text-gray-600">Ingrese el código enviado a su teléfono.</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Código de verificación</label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 tracking-widest text-center text-lg font-mono" value={verCode} onChange={e => setVerCode(e.target.value)} placeholder="000000" maxLength={8} disabled={pwStep === 'validating'} />
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={pwStep === 'validating'} className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-medium py-2 rounded-lg transition">
                    {pwStep === 'validating' ? 'Validando…' : 'Verificar código'}
                  </button>
                  <button type="button" onClick={cancelPw} className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm py-2 rounded-lg transition">Cancelar</button>
                </div>
                <button type="button" onClick={handleSendCode} className="w-full text-xs text-purple-600 hover:underline">¿No recibiste el código? Reenviar</button>
              </form>
            )}
            {(pwStep === 'newpw' || pwStep === 'saving') && (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <p className="text-sm text-gray-600">Código verificado ✓ — ingrese su nueva contraseña.</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nueva contraseña</label>
                  <input type="password" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Mínimo 8 caracteres" disabled={pwStep === 'saving'} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Confirmar contraseña</label>
                  <input type="password" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repita la contraseña" disabled={pwStep === 'saving'} />
                  {newPw && confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>}
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={pwStep === 'saving'} className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-medium py-2 rounded-lg transition">
                    {pwStep === 'saving' ? 'Guardando…' : 'Guardar contraseña'}
                  </button>
                  <button type="button" onClick={cancelPw} disabled={pwStep === 'saving'} className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm py-2 rounded-lg transition">Cancelar</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ── Fila 2: Mi Comercio + Reglas de Comisión ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Mi Comercio */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <CardHeader
            gradient="bg-gradient-to-r from-teal-600 to-teal-500"
            icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            title="Mi Comercio"
            action={!editingStore && store ? (
              <button onClick={() => { setEditingStore(true); setStoreMsg(null); }} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg transition">
                Editar
              </button>
            ) : undefined}
          />

          <div className="p-5">
            {storeMsg && <Alert type={storeMsg.type} message={storeMsg.text} />}

            {loadingStore ? (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
                <Spinner /> Cargando datos…
              </div>
            ) : store ? (
              editingStore ? (
                /* ── Edit mode ── */
                <form onSubmit={handleSaveStore} className="space-y-3">
                  {/* Logo upload */}
                  <div className="flex flex-col items-center gap-2 mb-2">
                    <div className="relative">
                      <img
                        src={currentLogo}
                        alt="Logo"
                        className="h-20 w-20 rounded-xl object-cover border-2 border-gray-100 shadow-sm"
                        onError={e => { const t = e.target as HTMLImageElement; t.onerror = null; t.src = '/assets/logos/logo.svg'; }}
                      />
                      {uploadingLogo && (
                        <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center">
                          <Spinner color="text-teal-600" />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 disabled:opacity-50"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      {uploadingLogo ? 'Subiendo…' : 'Cambiar logo'}
                    </button>
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  </div>

                  <LabelInput label="Nombre del comercio" value={sName} onChange={setSName} placeholder="Nombre" disabled={savingStore} />
                  <LabelInput label="Dirección" value={sAddress} onChange={setSAddress} placeholder="Dirección completa" disabled={savingStore} />
                  <LabelInput label="Referencia" value={sReference} onChange={setSReference} placeholder="Referencia (opcional)" disabled={savingStore} />
                  <div className="grid grid-cols-2 gap-3">
                    <LabelInput label="Latitud" value={sLat} onChange={setSLat} placeholder="18.486057" disabled={savingStore} />
                    <LabelInput label="Longitud" value={sLng} onChange={setSLng} placeholder="-69.931212" disabled={savingStore} />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button type="submit" disabled={savingStore} className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-medium py-2 rounded-lg transition">
                      {savingStore ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                    <button type="button" disabled={savingStore} onClick={() => { setEditingStore(false); setStoreMsg(null); setSName(store.name); setSAddress(store.address || ''); setSReference(store.reference || ''); setSLat(store.latitude || ''); setSLng(store.longitude || ''); }} className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm py-2 rounded-lg transition">
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                /* ── View mode ── */
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="relative">
                      <img
                        src={currentLogo}
                        alt="Logo de tienda"
                        className="h-24 w-24 rounded-2xl object-cover border-2 border-gray-100 shadow-sm"
                        onError={e => { const t = e.target as HTMLImageElement; t.onerror = null; t.src = '/assets/logos/logo.svg'; }}
                      />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        title="Cambiar logo"
                        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-teal-500 hover:bg-teal-600 flex items-center justify-center shadow transition disabled:opacity-50"
                      >
                        {uploadingLogo
                          ? <Spinner color="text-white" />
                          : <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        }
                      </button>
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                    </div>
                  </div>
                  <div className="text-center -mt-1">
                    <p className="font-semibold text-gray-900">{store.name}</p>
                    {store.zone && <p className="text-xs text-teal-600 font-medium">{store.zone}</p>}
                  </div>
                  <div className="grid grid-cols-1 gap-3 pt-2 border-t border-gray-50">
                    <Field label="Dirección" value={store.address} />
                    <Field label="Referencia" value={store.reference} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Latitud" value={store.latitude} />
                      <Field label="Longitud" value={store.longitude} />
                    </div>
                  </div>
                </div>
              )
            ) : (
              <p className="text-center text-gray-400 text-sm py-6">No se pudo cargar la información del comercio.</p>
            )}
          </div>
        </div>

        {/* Reglas de Comisión */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <CardHeader
            gradient="bg-gradient-to-r from-amber-500 to-orange-500"
            icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>}
            title="Regla de Comisión"
          />
          <div className="p-5">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-4">
              {RULE_TABS.map(t => (
                <button key={t.key} onClick={() => setRuleTab(t.key)} className={`flex-1 text-xs font-medium py-1.5 rounded-md transition ${ruleTab === t.key ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {loadingRules ? (
              <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm">
                <Spinner /> Cargando reglas…
              </div>
            ) : rules.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">Sin reglas configuradas.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {/* Header row */}
                <div className="flex items-center gap-3 px-2.5 py-1">
                  <div className="w-9 shrink-0" />
                  <p className="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Operador</p>
                  <p className="w-24 text-right text-xs font-semibold text-gray-400 uppercase tracking-wide">Comisión</p>
                </div>
                {rules.map((rule, i) => {
                  const isPercent = rule.type === '0' || rule.type === 0;
                  const display = rule.value !== undefined && rule.value !== null
                    ? `${rule.value}${isPercent ? '%' : ' RD$'}`
                    : '—';
                  return (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-amber-100 hover:bg-amber-50/30 transition">
                      {rule.logo ? (
                        <img
                          src={storageUrl(rule.logo)}
                          alt={rule.operator}
                          className="w-9 h-9 rounded-lg object-contain border border-gray-100 bg-white shrink-0"
                          onError={e => { const t = e.target as HTMLImageElement; t.onerror = null; t.src = '/assets/logos/logo.svg'; }}
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm shrink-0">
                          {rule.operator?.[0] || '?'}
                        </div>
                      )}
                      <p className="flex-1 text-sm font-medium text-gray-800 truncate">{rule.operator}</p>
                      <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-lg ${isPercent ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {display}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfilePage;
